import { applicationState, providerState, sessionState, validationState } from "@/state"

import { goto } from "@/util/navigation"
import SubmitFullApplyAction from "@/action/actions/SubmitFullApplyAction"
import SubmitPrequalifyAction from "@/action/actions/SubmitPrequalifyAction"
import SubmitPrescreenAction from "@/action/actions/SubmitPrescreenAction"
import ApplyResponseCode, { isAnyApprovalCode } from "@/model/apply/ApplyResponseCode"
import SwitchProviderAction from "@/action/actions/SwitchProviderAction"
import ApplyMode from "@/model/apply/ApplyMode"
import { determineApplyModeFromProviderKeyAndOperation } from "@/util/apply"
import router from "@/router"
import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
import Providers from "@/model/provider/Providers"
import ProviderError from "@/model/provider/ProviderError";
import accountState from "@/state/AccountState.js"
import ApplyOperation from "@/model/apply/ApplyOperation.js"

// noinspection JSUnusedGlobalSymbols
const handlers = {
    unknown: (action, response) => {
        console.error("Unknown response code", responseToCode(response))
        return goto("generalError").then(() => response)
    },
}

handlers[ApplyResponseCode.APPROVED] = () => {
    if (applicationState.applyMode === ApplyMode.FULL) {
        return goto(`/providers/${sessionState.providerKey}/paymentPlans`)
    } else {
        return goto(`/providers/${sessionState.providerKey}/approved`)
    }
}
handlers[ApplyResponseCode.DECLINED] = () => goto(`/providers/${sessionState.providerKey}/declined`)
handlers[ApplyResponseCode.ERROR] = () => goto(`/providers/${sessionState.providerKey}/error`)
handlers[ApplyResponseCode.NOOFFER] = () => {
    const primaryProvider = Providers[DynamicProviderKey.extractProviderKey(providerState.primaryProvider.providerKey)]
    if (primaryProvider?.supportsFullAppMode) {
        return goto(`/providers/${sessionState.providerKey}/nooffer`)
    } else {
        return goto(`/providers/${sessionState.providerKey}/declined`)
    }
}
handlers[ApplyResponseCode.PENDING] = () => goto(`/providers/${sessionState.providerKey}/pending`)
handlers[ApplyResponseCode.PREAPPROVED] = () => goto(`/providers/${sessionState.providerKey}/preapproved`)
handlers[ApplyResponseCode.PREQUALIFIED] = () => goto(`/providers/${sessionState.providerKey}/prequalified`)
handlers["resume"] = (_, response) => goto(`/providers/${response.provider.providerKey}/resume?from=decision`)
handlers["start"] = (_, response) =>
    goto(`/providers/${response.provider.providerKey}/modes/${response.applyMode}/start`)
handlers["existing_account"] = () => goto(`/providers/${sessionState.providerKey}/existingAccount`)

function handleResponse(action, response) {
    if (action.acceptOffer &&
        providerState.getError(sessionState.providerKey, ProviderError.ACCEPT_OFFER_SOFT_ERROR)) return handleAcceptOfferError()
    if (!action.cascade) return response
    if (!response.provider) return response.unknown(action, response)

    return handleCascade(action, response)
}

function handleException(action, error) {
    console.error("Error", error)

    if (error?.response?.status === 400) {
        return handleValidationException(action, error)
    }

    if (providerState.getError(sessionState.providerKey, ProviderError.ACCEPT_OFFER_ERROR)) {
        return handleAcceptOfferError()
    }

    return handleGeneralError(action, error)

}

function handleGeneralError(action, error) {
    // noinspection JSIgnoredPromiseFromCall
    goto({ name: "generalError" })
    throw error
}

function handleAcceptOfferError() {
    // noinspection JSIgnoredPromiseFromCall
    goto(`/providers/${sessionState.providerKey}/acceptOfferError`)
}

function handleValidationException(action, error) {
    // TODO rbohn: Take a second look
    if (sessionState.session.flowType !== "apply") {
        return handleGeneralError(action, error)
    }


    let validationResponse = error.response.data

    validationState.invalidApplicants = ["primary", "joint"].filter(applicantType => {

        for (let error of validationResponse.fieldErrors) {
            if (error.field.startsWith(applicantType)) {
                return true
            }
        }

        return false
    })

    validationState.resubmitRoute = router.currentRoute
    validationState.validationErrorResponse = validationResponse

    if (!validationState.invalidApplicants.length) {
        return
    }

    if (router.currentRoute.name !== "correctErrors") {
        return goto({ path: `/providers/${sessionState.providerKey}/correctErrors`, query: { applicantType: validationState.invalidApplicants[0] } })
    } else {
        // do nothing
        return
    }
}

async function handleCascade(action, response) {
    const finalResponse = await handleCascadeResponse(action, response, response, [])
    const finalProviderKey = finalResponse.provider?.providerKey

    if (response.provider?.existingAccount) {
        sessionState.nextProviderKeyAfterExistingAccount = finalProviderKey
        const handler = handlers["existing_account"]
        return handler(action, response)
    }

    let responseCode = responseToCode(finalResponse)
    if (finalProviderKey && finalProviderKey !== sessionState.providerKey) {
        console.log("SETTING provider key to", finalProviderKey)
        sessionState.dynamicProviderKey = new DynamicProviderKey(finalProviderKey)
    }

    if (finalResponse.start || finalResponse.resume) {
        await invoke(new SwitchProviderAction(finalResponse.provider.providerKey, finalResponse.applyMode))
    } else if (isAnyApprovalCode(finalResponse.provider?.responseCode)) {
        const determinedApplyMode = determineApplyModeFromResponse(finalResponse)
        if (determinedApplyMode !== applicationState.applyMode) {
            await invoke(new SwitchProviderAction(sessionState.dynamicProviderKey, determinedApplyMode))
        }
        applicationState.applyMode = determinedApplyMode || applicationState.applyMode
    }

    // Aspen Business Rule - errors and pending are treated as a no offer
    if (action.preauth) {
        switch (responseCode) {
            case ApplyResponseCode.ERROR:
            case ApplyResponseCode.PENDING:
                responseCode = ApplyResponseCode.NOOFFER
        }
    }

    //This block refers to if there is an existing account.
    // If the next provider is the current provider and the preauth response is a no offer
    // then we should display the payment plans screen because the consumer may want to still use their existing account.
    if (
        sessionState.providerKey === sessionState.nextProviderKeyAfterExistingAccount &&
        accountState.account.providerKey &&
        responseCode === ApplyResponseCode.NOOFFER
    ) {
        return goto(`/providers/${accountState.account.providerKey}/paymentPlans`)
    }

    const handler = handlers[responseCode] || handlers.unknown
    return handler(action, finalResponse)
}

async function handleCascadeResponse(action, response, fallbackResponse, previousResponses) {
    const next = response.next

    if (!next) {
        return response
    }

    if (next.resultType !== "provider") {
        console.warn("Unhandled cascade result type", next.resultType)
        return response
    }

    if (!next.autoSubmit) {
        return {
            provider: next.provider,
            applyMode: next.applyMode,
            start: !next.resume,
            resume: !!next.resume,
        }
    }

    if (response?.provider?.existingAccount === true &&
        response?.provider?.operation === ApplyOperation.PREQUALIFY
    ) {
        return next
    }

    previousResponses.push(response)

    const nextAction = buildActionFromApplyMode(next.applyMode, next.provider.providerKey)

    if (!nextAction) {
        console.warn("Unrecognized apply mode", next.applyMode)
        return response
    }

    try {
        // noinspection JSValidateTypes
        const nextResponse = await nextAction.invoke()

        if (nextResponse.next) {
            return await handleCascadeResponse(
                nextAction,
                nextResponse,
                fallbackResponse,
                previousResponses.concat([response])
            )
        }

        // if we have prior approvals, prefer them and sort out later
        const firstApprovedResponse = previousResponses.find(prevResponse =>
            isAnyApprovalCode(responseToCode(prevResponse))
        )

        if (firstApprovedResponse) {
            return firstApprovedResponse
        }

        if (isAnyApprovalCode(responseToCode(nextResponse))) {
            return nextResponse
        }

        return fallbackResponse
    } catch (e) {
        console.error(`Error submitting ${next.applyMode} to ${nextAction.providerKey}`, e)
        return fallbackResponse
    }
}

function responseToCode(response) {
    if (response.resume) return "resume"
    if (response.start) return "start"
    return response?.provider?.responseCode
}

const buildActionFromApplyMode = function (applyMode, providerKey, application = {}) {
    switch (applyMode) {
        case ApplyMode.FULL:
            return new SubmitFullApplyAction(providerKey, application)
        case ApplyMode.PREQUALIFY:
            return new SubmitPrequalifyAction(providerKey, application)
        case ApplyMode.PRESCREEN:
            return new SubmitPrescreenAction(providerKey, application)
    }

    return null
}

async function invoke(action, onSuccess = handleResponse, onError = handleException) {
    try {
        const response = await action.invoke()
        return onSuccess(action, response)
    } catch (e) {
        return onError(action, e)
    }
}

function determineApplyModeFromResponse(response) {
    if (!response.provider) return applicationState.applyMode
    const determinedApplyMode = determineApplyModeFromProviderKeyAndOperation(response.provider.providerKey, response.provider.operation)
    return (
        determinedApplyMode ||
        applicationState.applyMode
    )
}

export default {

    invoke(action) {
        return invoke(action)
    },
}
